<template>
  <div class="h-full flex flex-col">
    <l-modal-header
      :title="t('generate-invoice.new-company')"
      :show-back="true"
      @back="emit('back')"
    />

    <l-modal-content>
      <l-form ref="formRef" :validation-schema="formSchema" @submit="handleSave">
        <div class="flex gap-4">
          <l-form-field class="w-full" v-slot="{ field }" name="taxId">
            <l-form-label>{{ t('generate-invoice.tax-id') }}</l-form-label>
            <l-input v-bind="field" @blur="handleTaxIdBlur" />
            <l-form-error />
          </l-form-field>
          <l-form-field class="w-full" v-slot="{ field }" name="name">
            <l-form-label>{{ t('generate-invoice.name') }}</l-form-label>
            <l-input v-bind="field" />
            <l-form-error />
          </l-form-field>
        </div>

        <l-form-field v-slot="{ field }" name="address">
          <l-form-label>{{ t('generate-invoice.address') }}</l-form-label>
          <l-input v-bind="field" />
          <l-form-error />
        </l-form-field>
      </l-form>
    </l-modal-content>

    <l-modal-footer>
      <l-button class="w-full" @click="formRef?.requestSubmit()">
        {{ t('ctas.save') }}
      </l-button>
    </l-modal-footer>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { v4 as uuid } from 'uuid'
import { onMounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { z } from 'zod'

import {
  LButton,
  LForm,
  LFormError,
  LFormField,
  LFormLabel,
  LInput,
  LModalContent,
  LModalFooter,
  LModalHeader
} from '@last/core-ui/paprika'

import { legalEntitiesApi } from '@/api/client'
import { useConfigStore } from '@/store/config'
import type { Company } from '@/types'

import { zodErrorMessages } from '../reservations/zod-error-messages'

z.setErrorMap(zodErrorMessages)

const props = defineProps<{ company?: Company }>()
const emit = defineEmits<{
  save: [Company]
  back: []
}>()

const { t } = useI18n()
const formRef = useTemplateRef('formRef')
const configStore = useConfigStore()

const lastValidatedTaxId = ref<string>()
const lastValidatedName = ref<string>()

const schema = z
  .object({
    taxId: z.string().trim().nonempty(),
    name: z.string().trim().nonempty(),
    address: z.string().trim().nonempty()
  })
  .superRefine(async (data, ctx) => {
    if (!data.taxId) return

    if (data.taxId === lastValidatedTaxId.value && data.name === lastValidatedName.value) {
      return
    }

    lastValidatedTaxId.value = data.taxId
    lastValidatedName.value = data.name

    try {
      const result = await legalEntitiesApi.validateLegalEntity({
        validateLegalEntityRequest: {
          taxId: data.taxId,
          companyName: data.name || undefined,
          countryCode: configStore.config.locationCountryCode
        }
      })

      if (!result.isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['taxId'],
          message: t('generate-invoice.invalid-tax-id')
        })
      }
    } catch {
      // Error handled silently during validation
    }
  })

const formSchema = toTypedSchema(schema)

type FormValues = z.infer<typeof schema>

function handleSave(values: FormValues) {
  emit('save', { id: props.company?.id ?? uuid(), ...values })
}

async function handleTaxIdBlur() {
  if (!formRef.value) return

  const taxId = formRef.value.values.taxId
  if (!taxId) return

  try {
    const result = await legalEntitiesApi.validateLegalEntity({
      validateLegalEntityRequest: {
        taxId,
        countryCode: configStore.config.locationCountryCode as any
      }
    })

    if (result.isValid && result.companyName && !formRef.value.values.name) {
      formRef.value.setFieldValue('name', result.companyName)
    }
  } catch {
    // Error handled silently during validation
  }
}

onMounted(() => {
  if (props.company) {
    formRef.value?.setValues({
      taxId: props.company.taxId,
      name: props.company.name,
      address: props.company.address
    })
  }
})
</script>
